import { FileCheck } from 'lucide-react'
import type { Deal } from '@/types'
import { maskCnpjDisplay } from '@/utils/dealDisplay'
import { InfoField } from './InfoField'
import { useLegalDataSection } from './useLegalDataSection'

const dateInputClass =
  'w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent'

export function LegalDataSection({
  deal,
  onDealPatched,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
}) {
  const legal = useLegalDataSection(deal, onDealPatched)

  return (
    <div className="glass-card p-4 space-y-3">
      <h3 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <FileCheck size={14} />
        Dados Legais (Contrato)
      </h3>

      {legal.empresaCnpj && (
        <InfoField label="CNPJ da empresa" value={maskCnpjDisplay(legal.empresaCnpj)} />
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
          Email Financeiro
        </label>
        <input
          type="email"
          value={legal.emailFinanceiro}
          onChange={(e) => legal.setEmailFinanceiro(e.target.value)}
          onBlur={() => void legal.persist()}
          placeholder="financeiro@empresa.com"
          className={dateInputClass}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
          Representante Legal
        </label>
        <input
          type="text"
          value={legal.representanteLegal}
          onChange={(e) => legal.setRepresentanteLegal(e.target.value)}
          onBlur={() => void legal.persist()}
          placeholder="Nome do representante legal"
          className={dateInputClass}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Assinado em
          </label>
          <input
            type="date"
            value={legal.assinadoEm}
            onChange={(e) => {
              legal.setAssinadoEm(e.target.value)
              legal.scheduleSave()
            }}
            className={dateInputClass}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Início do Contrato
          </label>
          <input
            type="date"
            value={legal.inicioContrato}
            onChange={(e) => {
              legal.setInicioContrato(e.target.value)
              legal.scheduleSave()
            }}
            className={dateInputClass}
          />
        </div>

        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Data do primeiro pagamento
          </label>
          <input
            type="date"
            value={legal.primeiroPagamento}
            onChange={(e) => {
              legal.setPrimeiroPagamento(e.target.value)
              legal.scheduleSave()
            }}
            className={dateInputClass}
          />
        </div>
      </div>

      {legal.saving && (
        <div className="flex items-center gap-2 text-xs text-text-label">
          <div className="w-3 h-3 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          Salvando...
        </div>
      )}
    </div>
  )
}
